import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Church, Search, Users, CreditCard, Wallet, ShieldCheck, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { StatCard, Banner, Loading, fmtDate, KV } from '@/components/OpsUI';

export default function AppChurchesPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [known, setKnown] = useState(null);
  const [lookupId, setLookupId] = useState('');
  const [church, setChurch] = useState(null);
  const [loadingChurch, setLoadingChurch] = useState(false);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState('');
  const [syncing, setSyncing] = useState(false);

  const loadKnown = useCallback(async (refresh = false) => {
    setSyncing(true);
    try {
      const { data } = await api.get('/ops/known-churches', { params: refresh ? { refresh: true } : {} });
      setKnown(data);
      if (data.sync_error) toast.error(data.sync_error);
    } catch (e) {
      toast.error(errText(e, 'No se pudo cargar el directorio de iglesias'));
    } finally { setSyncing(false); }
  }, []);

  const loadChurch = useCallback(async (id) => {
    if (!id) return;
    setLoadingChurch(true); setError(null); setChurch(null);
    try {
      const { data } = await api.get(`/ops/churches/${id}`);
      setChurch(data);
    } catch (e) {
      setError(errText(e));
    } finally { setLoadingChurch(false); }
  }, []);

  useEffect(() => { loadKnown(); }, [loadKnown]);

  useEffect(() => {
    const id = params.get('id');
    if (id) { setLookupId(id); loadChurch(id); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLookup = (e) => {
    e?.preventDefault?.();
    if (!lookupId.trim()) return;
    setParams({ id: lookupId.trim() });
    loadChurch(lookupId.trim());
  };

  const members = church?.members || {};
  const shownChurches = (known?.items || []).filter((c) => `${c.name || ''} ${c.slug || ''}`.toLowerCase().includes(filter.toLowerCase()));
  const sub = church?.subscription || {};
  const caura = church?.caurapay || {};
  const balance = church?.balance || {};

  return (
    <div className="space-y-6" data-testid="app-churches-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Church className="h-6 w-6 text-[hsl(var(--primary))]" /> {t('nav_app_churches')}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Tu Iglesia App · directorio y fichas</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => loadKnown(true)} disabled={syncing} data-testid="app-churches-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Sincronizando con la App...' : t('refresh')}
        </Button>
      </div>

      <form onSubmit={doLookup} className="flex gap-2 max-w-xl">
        <Input
          value={lookupId}
          onChange={(e) => setLookupId(e.target.value)}
          placeholder={t('ops_lookup_church')}
          data-testid="ops-church-lookup-input"
        />
        <Button type="submit" data-testid="ops-church-lookup-btn">
          <Search className="h-4 w-4 mr-1" /> {t('search')}
        </Button>
      </form>

      {error && <Banner kind="error" message={String(error)} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Directorio */}
        <Card className="bg-card border border-border lg:col-span-1 h-fit">
          <div className="p-4 border-b border-border space-y-2">
            <div className="text-sm font-display font-semibold" data-testid="app-churches-count">{t('ops_known_churches')} ({known?.count ?? 0})</div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))]" data-testid="app-churches-synced-at">
              {known?.synced_at ? `Sincronizado con la App: ${fmtDate(known.synced_at)}` : 'Aún no sincronizado con la App'}
            </div>
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrar por nombre" className="h-8 text-xs" data-testid="app-churches-filter" />
          </div>
          <div className="max-h-[480px] overflow-auto">
            {shownChurches.length === 0 ? (
              <div className="p-4 text-xs text-[hsl(var(--muted-foreground))]">{syncing ? t('loading') : 'No hay iglesias. Pulsa Actualizar para sincronizar con la App.'}</div>
            ) : shownChurches.map((c) => (
              <button
                key={c.id}
                onClick={() => { setLookupId(c.id); setParams({ id: c.id }); loadChurch(c.id); }}
                className="w-full text-left px-4 py-3 border-b border-border/40 hover:bg-accent/50 transition-colors duration-150"
                data-testid={`ops-church-item-${c.id}`}
              >
                <div className="text-sm font-medium truncate">{c.name || c.slug || c.id}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                  {c.members_total != null ? `${c.members_total} ${c.members_total === 1 ? 'miembro' : 'miembros'}` : c.id}{c.created_at ? ` · desde ${fmtDate(c.created_at)}` : ''}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Ficha */}
        <div className="lg:col-span-2">
          {loadingChurch ? <Loading label={t('loading')} /> : church ? (
            <div className="space-y-5" data-testid="ops-church-detail">
              <Card className="p-5 bg-card border border-border">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xl font-display font-semibold">{church.name || '\u2014'}</div>
                    <div className="text-sm text-[hsl(var(--muted-foreground))] font-mono">{church.slug}</div>
                  </div>
                  {church.dispersion_configured != null && (
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${church.dispersion_configured ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10' : 'text-zinc-300 border-zinc-500/30 bg-zinc-500/10'}`}>
                      <ShieldCheck className="h-3.5 w-3.5" /> {t('ops_dispersion')}
                    </span>
                  )}
                </div>
                <div className="mt-3">
                  <KV k="ID" v={church.id} />
                  <KV k="UUID" v={church.uuid} />
                  <KV k={t('email')} v={church.email} />
                  <KV k="Creada" v={fmtDate(church.created_at)} />
                </div>
              </Card>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Users} label={t('ops_members')} value={members.total ?? '\u2014'} />
                <StatCard icon={Wallet} label="paid VES" value={(balance.paid_ves ?? 0).toLocaleString()} />
                <StatCard icon={Wallet} label="paid USD" value={(balance.paid_usd ?? 0).toLocaleString()} />
                <StatCard icon={CreditCard} label={t('ops_plan')} value={sub.plan ?? '\u2014'} sub={sub.status} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Card className="p-4 bg-card border border-border">
                  <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{t('ops_members')}</div>
                  <KV k="Total" v={members.total} />
                  {Object.entries(members.by_status || {}).map(([k, v]) => <KV key={k} k={k} v={v} />)}
                </Card>
                <Card className="p-4 bg-card border border-border">
                  <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{t('ops_subscription')}</div>
                  <KV k={t('ops_plan')} v={sub.plan} />
                  <KV k={t('ops_sub_status')} v={sub.status} />
                  <KV k={t('ops_expires')} v={fmtDate(sub.expires_at)} />
                </Card>
                <Card className="p-4 bg-card border border-border">
                  <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{t('ops_caurapay')}</div>
                  <KV k={t('ops_provisioned')} v={caura.provisioned ? t('yes') : t('no')} />
                  <KV k={t('ops_project_id')} v={caura.project_id} />
                  <KV k={t('ops_code')} v={caura.code} />
                  <KV k={t('ops_api_key')} v={caura.api_key_last4 ? `\u2022\u2022\u2022\u2022 ${caura.api_key_last4}` : null} />
                </Card>
                <Card className="p-4 bg-card border border-border">
                  <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{t('ops_balance')}</div>
                  <KV k="paid VES" v={(balance.paid_ves ?? 0).toLocaleString()} />
                  <KV k="paid USD" v={(balance.paid_usd ?? 0).toLocaleString()} />
                </Card>
              </div>
            </div>
          ) : (
            <Card className="p-10 bg-card border border-border text-center text-sm text-[hsl(var(--muted-foreground))]">
              Elige una iglesia del directorio o búscala por su ID.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
